import axios from 'axios';

import { USER_API_URL, ADMIN_API_URL } from '../../Server/Config';
import { waitForFirebaseAuth } from '../../lib/firebaseClients';
import { getStoredSession } from '../../lib/Token';
import { USER_TYPES } from '../../Constant/Enums';

// Do NOT force-refresh: getIdToken() returns Firebase's in-memory cached token
// and only hits the securetoken endpoint when it is near expiry. Passing `true`
// here refreshed on every request, hammering Google's API.
const tokenForUserType = async userType => {
  const auth = await waitForFirebaseAuth(userType);
  const user = auth?.currentUser;
  return user ? user.getIdToken() : null;
};

const getFirebaseToken = async () => {
  const session = getStoredSession();

  // Logged-in case: a session type is stored — use that app's token.
  if (session?.type) {
    return tokenForUserType(session.type);
  }

  // Registration fallback: there is no stored session yet, but the applicant
  // was just signed into the professional Firebase app at the Personal Details
  // step. Attach that token so createProfessional / createService authenticate
  // without us having to first persist a session flag. The professional is
  // signed out on completion/abandonment, so this never leaks past registration.
  return tokenForUserType(USER_TYPES.PROFESSIONAL);
};

// Bridge so the plain-JS Axios layer can surface failures through the
// React toast system. The ToastProvider registers its showToastMessage here.
let apiErrorHandler = null;

export const setApiErrorHandler = handler => {
  apiErrorHandler = typeof handler === 'function' ? handler : null;
};

const notifyApiError = message => {
  if (apiErrorHandler) apiErrorHandler(message, 'error');
};

const resolveErrorMessage = error => {
  const graphqlMessage = error?.response?.data?.errors?.[0]?.message;
  if (graphqlMessage) return graphqlMessage;
  if (error?.response?.status) {
    return `Request failed (${error.response.status}). Please try again.`;
  }
  return 'Network error — please check your connection and try again.';
};

const normalizeRequest = ({ query, variables }) => {
  if (query && typeof query === 'object' && query.query) {
    return {
      query: query.query,
      variables: variables ?? query.variables,
    };
  }

  return { query, variables };
};

const makeRequest = baseURL => async request => {
  const { query, variables } = normalizeRequest(request);
  const token = await getFirebaseToken();
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return axios
    .post(baseURL, { query, variables }, { headers })
    .then(response => response.data)
    .catch(error => {
      console.error(error);
      notifyApiError(resolveErrorMessage(error));
      throw error;
    });
};

export const UserAxios = makeRequest(USER_API_URL);
export const AdminAxios = makeRequest(ADMIN_API_URL);

export default UserAxios;
